/* import __COLOCATED_TEMPLATE__ from './agent-select.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'ember-intl/services/intl';
import { IntegrationType, RESPONSE_PROVIDED_ERRORS } from 'embercom/objects/standalone/constants';
import { put } from 'embercom/lib/ajax';

interface Signature {
  Args: {
    setUpdatingFinAgent: (isLoading: boolean) => void;
  };
}

export default class AgentSelectComponent extends Component<Signature> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;

  @tracked newAgentId?: number;

  get freshdeskConfig() {
    return this.finStandaloneService.freshdeskConfig!;
  }

  get ticketsSetupData() {
    return this.finStandaloneService.freshdeskTicketsSetupData;
  }

  get isLoading() {
    return (
      this.finStandaloneService.isLoadingFreshdeskTicketsSetupData && !this.adminOptions.length
    );
  }

  get adminOptions() {
    let agents = this.ticketsSetupData.all_possible_fin_users?.map((agent) => ({
      value: agent.id,
      text: agent.name,
    }));
    return agents || [];
  }

  get selectedAgentName(): string | undefined {
    if (this.isLoading) {
      return undefined;
    }

    return this.adminOptions.find((option) => option.value === this.selectedAgentId)?.text;
  }

  get selectedAgentId(): number | undefined {
    if (this.freshdeskConfig.ticketsIntegration.finFreshdeskUserId) {
      return this.freshdeskConfig.ticketsIntegration.finFreshdeskUserId;
    }
    // Also check the setup data for the currently selected agent
    if (this.ticketsSetupData.fin_user_id) {
      return parseInt(this.ticketsSetupData.fin_user_id, 10);
    }
    return undefined;
  }

  get isSaving() {
    return taskFor(this.updateAgentId).isRunning;
  }

  @action setAgentId(agentId: number) {
    this.newAgentId = agentId;
    taskFor(this.updateAgentId).perform();
  }

  @task({ restartable: true })
  *updateAgentId(): TaskGenerator<void> {
    if (!this.newAgentId) {
      this.notificationsService.notifyInfo(
        this.intl.t('standalone.freshdesk.deploy.tickets.fin-identity.choose-an-agent-first'),
      );
      return;
    }

    try {
      this.args.setUpdatingFinAgent(true);

      let freshdeskIntegrationId = this.finStandaloneService.standaloneIntegrations.find(
        (integration) => integration.integrationType === IntegrationType.FreshdeskTickets,
      )?.integrationId;

      if (!freshdeskIntegrationId) {
        return;
      }

      yield put(
        `/ember/standalone/integrations/${IntegrationType.FreshdeskTickets}/${freshdeskIntegrationId}/fin_user_identity?app_id=${this.finStandaloneService.app.id}`,
        {
          fin_user_id: this.newAgentId.toString(),
        },
      );

      // Update the local state to reflect the change
      this.finStandaloneService.freshdeskTicketsSetupData = {
        ...this.finStandaloneService.freshdeskTicketsSetupData,
        fin_user_id: this.newAgentId.toString(),
      };

      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.freshdesk.deploy.tickets.fin-identity.agent-set-success', {
          agentName: this.selectedAgentName,
        }),
      );
    } catch (e) {
      this.notificationsService.notifyResponseError(
        e,
        {
          default: this.intl.t(
            'standalone.freshdesk.deploy.tickets.fin-identity.error-setting-agent',
          ),
        },
        {
          responseProvidedErrors: RESPONSE_PROVIDED_ERRORS,
        },
      );
      console.error(e);
    } finally {
      this.args.setUpdatingFinAgent(false);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Freshdesk::Deploy::Tickets::Integrate::FinIdentity::AgentSelect': typeof AgentSelectComponent;
  }
}
